import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Loader2,
  CreditCard,
  Building2,
  Shield
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

// Get API base URL from environment variable or use default
const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || '/api/v1';

interface ConnectStatus {
  connected: boolean;
  account_id?: string;
  onboarded: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  requirements?: {
    currently_due: string[];
    eventually_due: string[];
    past_due: string[];
  };
}

export const StripeConnectSetup = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Check if returning from Stripe onboarding and invalidate cache
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === 'true' || params.get('refresh') === 'true') {
      // Invalidate cache to fetch fresh Connect status
      queryClient.invalidateQueries({ queryKey: ['stripe-connect-status'] });

      // Show success message if just connected
      if (params.get('connected') === 'true') {
        toast({
          title: 'Account Connected',
          description: 'Your Stripe account has been successfully connected.',
          variant: 'default',
        });
      }

      // Clean up URL params
      const url = new URL(window.location.href);
      url.searchParams.delete('connected');
      url.searchParams.delete('refresh');
      window.history.replaceState({}, '', url.toString());
    }
  }, [queryClient, toast]);

  // Fetch Connect account status
  const { data: connectStatus, isLoading, error, refetch } = useQuery<ConnectStatus>({
    queryKey: ['stripe-connect-status'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/payments/connect/status`, {
        credentials: 'include', // Send cookies for authentication
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || 'Failed to fetch Connect status';
        throw new Error(errorMessage);
      }

      return response.json();
    },
    staleTime: 30000, // 30 seconds
    retry: 2, // Retry failed requests twice
  });

  // Create onboarding link
  const onboardMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_BASE_URL}/payments/connect/onboard`, {
        method: 'POST',
        credentials: 'include', // Send cookies for authentication
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create onboarding link');
      }

      return response.json();
    },
    onSuccess: (data) => {
      setIsRedirecting(true);
      // Redirect to Stripe onboarding
      window.location.href = data.url;
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Create dashboard link
  const dashboardMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_BASE_URL}/payments/connect/dashboard`, {
        method: 'POST',
        credentials: 'include', // Send cookies for authentication
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create dashboard link');
      }

      return response.json();
    },
    onSuccess: (data) => {
      // Open Stripe dashboard in new tab
      window.open(data.url, '_blank');
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleStartOnboarding = () => {
    onboardMutation.mutate();
  };

  const handleOpenDashboard = () => {
    dashboardMutation.mutate();
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Stripe Connect Setup</CardTitle>
          <CardDescription>Loading your payout account status...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load Connect account status';
    const isConfigError = errorMessage.includes('configuration') || errorMessage.includes('authentication');
    const isNetworkError = errorMessage.includes('reach') || errorMessage.includes('network');

    return (
      <Card>
        <CardHeader>
          <CardTitle>Stripe Connect Setup</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>{errorMessage}</p>
              {isConfigError && (
                <p className="text-sm">
                  This appears to be a system configuration issue. Please contact support for assistance.
                </p>
              )}
              {isNetworkError && (
                <p className="text-sm">
                  Unable to connect to payment services. Please check your internet connection and try again.
                </p>
              )}
            </AlertDescription>
          </Alert>
          <div className="flex gap-3">
            <Button onClick={() => refetch()} variant="outline" className="flex-1">
              Try Again
            </Button>
            <Button
              onClick={() => window.open('mailto:support@voicethera.com?subject=Stripe Connect Error&body=' + encodeURIComponent(errorMessage), '_blank')}
              variant="outline"
              className="flex-1"
            >
              Contact Support
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isFullyOnboarded = connectStatus?.onboarded && connectStatus?.payouts_enabled;
  const hasPendingRequirements = (connectStatus?.requirements?.currently_due?.length ?? 0) > 0 ||
                                  (connectStatus?.requirements?.past_due?.length ?? 0) > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-primary" />
          Payout Account Setup
        </CardTitle>
        <CardDescription>
          Connect your Stripe account to receive referral commission payouts
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status Overview */}
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Account Status</span>
            </div>
            {connectStatus?.connected ? (
              isFullyOnboarded ? (
                <Badge className="bg-green-500">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Active
                </Badge>
              ) : (
                <Badge variant="secondary">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Setup Required
                </Badge>
              )
            ) : (
              <Badge variant="outline">Not Connected</Badge>
            )}
          </div>

          {connectStatus?.connected && (
            <>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Verification Status</span>
                </div>
                {connectStatus.details_submitted ? (
                  <Badge variant="secondary">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Verified
                  </Badge>
                ) : (
                  <Badge variant="outline">Pending</Badge>
                )}
              </div>

              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Payouts</span>
                </div>
                {connectStatus.payouts_enabled ? (
                  <Badge className="bg-green-500">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Enabled
                  </Badge>
                ) : (
                  <Badge variant="outline">Not Enabled</Badge>
                )}
              </div>
            </>
          )}
        </div>

        {/* Pending Requirements Alert */}
        {connectStatus?.connected && hasPendingRequirements && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Action Required</AlertTitle>
            <AlertDescription>
              Your Stripe account needs additional information to enable payouts.
              Please complete the setup process.
            </AlertDescription>
          </Alert>
        )}

        {/* Not Connected State */}
        {!connectStatus?.connected && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Setup Required</AlertTitle>
            <AlertDescription>
              To receive referral commission payouts, you need to connect a Stripe account.
              This is a secure process handled by Stripe and takes just a few minutes.
            </AlertDescription>
          </Alert>
        )}

        {/* Onboarding Information */}
        {!isFullyOnboarded && (
          <div className="space-y-3 p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
            <h4 className="font-semibold text-sm text-blue-900 dark:text-blue-100">
              What you'll need:
            </h4>
            <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
              <li>• Government-issued ID or passport</li>
              <li>• Bank account information for deposits</li>
              <li>• Personal information (name, address, date of birth)</li>
              <li>• Tax identification number (SSN or EIN)</li>
            </ul>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          {!connectStatus?.connected || !isFullyOnboarded ? (
            <Button
              onClick={handleStartOnboarding}
              disabled={onboardMutation.isPending || isRedirecting}
              className="flex-1"
            >
              {onboardMutation.isPending || isRedirecting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Redirecting to Stripe...
                </>
              ) : (
                <>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {connectStatus?.connected ? 'Complete Setup' : 'Start Setup'}
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleOpenDashboard}
              disabled={dashboardMutation.isPending}
              variant="outline"
              className="flex-1"
            >
              {dashboardMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open Stripe Dashboard
                </>
              )}
            </Button>
          )}
        </div>

        {/* Security Notice */}
        <div className="text-xs text-muted-foreground space-y-1 pt-4 border-t">
          <p className="font-medium">🔒 Secure Connection</p>
          <p>
            Your account setup is handled directly by Stripe, our secure payment processor.
            VoiceThera never has access to your banking credentials.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
